import React, { useState, useMemo } from 'react';
import {
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  TextField,
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import { SelectField, DateField, DateTimeField } from '../../../../../Components/Field';
import { StepProps } from './types';
import { StepButtonBar } from './StepButtonBar';
import RichTextField from '../../../../../Components/Field/RichTextField';

interface Step8PreviewProps extends StepProps {
  onConfirmSave: () => Promise<void>;
  areas: { id: number; name: string }[];
  companyUnits: { id: number; name: string }[];
  complianceUsers: { id: number; name: string }[];
  editMode?: 'add' | 'edit' | 'delete' | null;
}

const PRIMARY = '#c4122f';

const Step8Preview: React.FC<Step8PreviewProps> = ({
  onBack,
  onCancel,
  currentData,
  onConfirmSave,
  areas,
  companyUnits,
  complianceUsers,
  editMode,
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isSaving, setIsSaving] = useState(false);

  const areaOptions = useMemo(() => areas.map((a) => ({ value: a.id, label: a.name })), [areas]);
  const companyUnitOptions = useMemo(() => companyUnits.map((u) => ({ value: u.id, label: u.name })), [companyUnits]);
  const complianceUserOptions = useMemo(() => complianceUsers.map((u) => ({ value: u.id, label: u.name })), [complianceUsers]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onConfirmSave();
    } catch (error) {
      console.error('Save failed:', error);
      setIsSaving(false);
    }
  };

  const formatDateValue = (date: string | Date | undefined): string => {
    if (!date) return '';
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
  };

  return (
    <Box style={{ width: '100%' }}>
      <Box
        style={{
          display: 'flex',
          width: '100%',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: 'stretch',
        }}
      >
        <Box
          style={{
            width: '100%',
            padding: isMobile ? theme.spacing(2) : theme.spacing(4),
            backgroundColor: '#f7f0f1',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            justifyContent: 'flex-start',
            alignItems: 'stretch',
          }}
        >
          <Typography
            variant="h6"
            style={{
              fontWeight: 600,
              marginBottom: theme.spacing(3),
              lineHeight: 1.4,
              fontSize: isMobile ? '16px' : '18px',
            }}
          >
            {formatMessage({
              id: 'law-portal.manage.requirements.preview',
              defaultMessage: 'Preview',
            })}
          </Typography>

          {/* Form Fields Layout - Disabled */}
          <Box
            style={{
              display: 'flex',
              width: '100%',
              gap: '24px',
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            {/* Left Column */}
            <Box style={{ flex: 1 }}>
              {/* Name and Area Row */}
              <Box
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: theme.spacing(2),
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Box style={{ flex: 1 }}>
                  <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                    {formatMessage({ id: 'form.name', defaultMessage: 'Name' })} *
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled
                    value={currentData.name || ''}
                    style={{ marginTop: '4px' }}
                  />
                </Box>
                <Box style={{ flex: 1 }}>
                  <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                    {formatMessage({ id: 'utils.filters.area', defaultMessage: 'Area' })} *
                  </Typography>
                  <SelectField
                    name="area"
                    options={areaOptions}
                    initialState={currentData.area ? { value: currentData.area, label: currentData.areaLabel || '' } : undefined}
                    errorTexts={[]}
                    onChange={() => {}}
                    isDisabled={true}
                    style={{ marginTop: '4px' }}
                  />
                </Box>
              </Box>

              {/* Effective From and To Row */}
              <Box
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: theme.spacing(2),
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Box style={{ flex: 1 }}>
                  <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                    {formatMessage({ id: 'law-portal.manage.requirements.effectiveFrom', defaultMessage: 'Effective From' })} *
                  </Typography>
                  <DateField
                    name="effectiveFrom"
                    placeholder={formatMessage({ id: 'law-portal.manage.requirements.effectiveFrom', defaultMessage: 'Effective From' })}
                    initialState={currentData.effectiveFrom ? { value: new Date(currentData.effectiveFrom) } : undefined}
                    onChange={() => {}}
                    errorTexts={[]}
                    isDisabled={true}
                    style={{ marginTop: '4px' }}
                  />
                </Box>
                <Box style={{ flex: 1 }}>
                  <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                    {formatMessage({ id: 'law-portal.manage.requirements.effectiveTo', defaultMessage: 'Effective To' })}
                  </Typography>
                  <DateField
                    name="effectiveTo"
                    placeholder={formatMessage({ id: 'law-portal.manage.requirements.effectiveTo', defaultMessage: 'Effective To' })}
                    initialState={currentData.effectiveTo ? { value: new Date(currentData.effectiveTo) } : undefined}
                    onChange={() => {}}
                    errorTexts={[]}
                    isDisabled={true}
                    style={{ marginTop: '4px' }}
                  />
                </Box>
              </Box>

              {editMode === 'edit' && currentData.effectiveFrom && currentData.effectiveTo && (
                <Box style={{ display: 'flex', gap: '16px', width: '100%' }}>
                  <Box style={{ flex: 1 }}>
                    <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                      {formatMessage({ id: 'law-portal.manage.requirements.effective-to-sendout-time', defaultMessage: 'Effective To Sendout Time' })}
                    </Typography>
                    <DateTimeField
                      name="effectiveToSendoutTime"
                      placeholder={formatMessage({ id: 'law-portal.manage.requirements.effective-to-sendout-time', defaultMessage: 'Effective To Sendout Time' })}
                      initialState={currentData.effectiveToSendoutTime ? { value: new Date(currentData.effectiveToSendoutTime) } : undefined}
                      onChange={() => {}}
                      errorTexts={[]}
                      isDisabled={true}
                      style={{ marginTop: '4px' }}
                    />
                  </Box>
                </Box>
              )}

                            {editMode !== 'edit' && (
                <Box
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: theme.spacing(2),
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Box style={{ flex: 1 }}>
                 <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                  {formatMessage({ id: 'form.selectCompanyUnits', defaultMessage: 'Company Unit' })} *
                </Typography>
                <SelectField
                  name="companyUnit"
                  options={companyUnitOptions}
                  initialState={(currentData as any).unitIds ? (currentData as any).unitIds.map((u: any) => ({ value: u.id, label: u.name })) : undefined}
                  errorTexts={[]}
                  onChange={() => {}}
                  isMulti={true}
                  isDisabled={true}
                  style={{ marginTop: '4px' }}
                />
                </Box>
                <Box style={{ flex: 1 }}>
                   <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666' }}>
                    {formatMessage({ id: 'utils.filters.complianceUsers', defaultMessage: 'Responsible' })}
                  </Typography>
                  <SelectField
                    name="responsible"
                    options={complianceUserOptions}
                    initialState={currentData.responsible && currentData.responsible.length > 0 ? currentData.responsible : undefined}
                    errorTexts={[]}
                    onChange={() => {}}
                    isMulti={true}
                    isDisabled={true}
                    style={{ marginTop: '4px' }}
                  />
                </Box>
              </Box>
            )}
            </Box>

            {/* Right Column - Short Description */}
            <Box style={{ flex: 1 }}>
              <Typography variant="caption" style={{ fontWeight: 600, fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                {formatMessage({ id: 'law-portal.manage.requirements.shortDescription', defaultMessage: 'Short Description' })} *
              </Typography>
              <Box style={{ marginTop: '4px' }}>
                <RichTextField
                  name="shortDescription"
                  initialState={currentData.shortDescription ? { value: currentData.shortDescription } : undefined}
                  onChange={() => {}}
                  errorTexts={[]}
                  isDisabled={true}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <StepButtonBar
        onCancel={onCancel}
        onBack={onBack}
        onNext={handleSave}
        nextLabel="form.save"
        nextDisabled={isSaving}
        formatMessage={formatMessage}
        showBack={true}
      />
    </Box>
  );
};

export default Step8Preview;
